import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Link, Navigate } from 'react-router-dom';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { MachineHistoryActions } from 'redux/actions/machines/MachineHistoryActions';
import { MachineActions } from 'redux/actions/settings/MachineActions';
import { MachineMaintenanceActions } from 'redux/actions/settings/MachineMaintenanceActions';

import { LoginUser } from 'models/LoginUser';
import { Machine } from 'models/Machine';
import { MachineDefect } from 'models/MachineDefect';
import { MachineHistory } from 'models/MachineHistory';
import { MachineMaintenance } from 'models/MachineMaintenance';
import { StoreModel } from 'models/StoreModel';
import { CurrentSubscription } from 'models/SubscriptionPlan';
import { Translations } from 'models/Translations';
import { WorkshopDefect } from 'models/ViewModels/WorkshopDefect';
import { WorkshopMaintenance } from 'models/ViewModels/WorkshopMaintenance';
import { WorkshopViewModel } from 'models/ViewModels/WorkshopViewModel';

import { Translate } from 'infrastructure/mapping/Translate';
import { PageContentHeader } from 'app/components/common/PageContentHeader';
import Translator from 'app/components/common/Translator';
import { withRouter } from "app/components/common/WithRouter";

interface Props {
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    fetchMachines: typeof MachineActions.fetchMachines;
    editMachineHistory: typeof MachineHistoryActions.editMachineHistory;
    addMachineHistory: typeof MachineHistoryActions.addMachineHistory;
    machineMaintenances: MachineMaintenance[];
    loginUser: LoginUser;
    machines: Machine[];
    machineHistories: MachineHistory[];
    processing: boolean;
    match: UrlParams;
    currentSubscription: CurrentSubscription | null;
    translations: Translations;
}

type Errors = { [fieldId: string]: string };

type Draft = {
    id: number;
    machine: Machine;
    serviceLife: number;
    date: Date;
    maintenanceIds: number[];
    defectIds: number[];
}

type State = {
    draft?: Draft;
    errors: Errors;
}

interface UrlParams {
    params: {
        id?: string;
    }
}

type Option<T> = { value: T, label: string };

class WorkshopComp extends React.Component<Props, State> {
    public readonly state: State = {
        errors: {}
    }

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machines && this.props.machineMaintenances && this.props.machineHistories
    }

    defectsEnabled() {
        return !!this.props.currentSubscription?.subscriptionPlan.defectsEnabled;
    }

    t(text: string) {
        return Translate.text(this.props.translations, text);
    }

    isEdit() {
        return !!this.props.match.params.id;
    }

    // the draft is derived from the store until the first edit, then it lives in state
    draft(): Draft | undefined {
        if (this.state.draft)
            return this.state.draft;
        if (!this.isEdit()) {
            const assigned = Machine.assignedTo(this.props.machines, this.props.loginUser.userData?.id);
            return { id: 0, machine: assigned ?? Machine.empty(), serviceLife: assigned?.serviceLife ?? NaN, date: new Date(), maintenanceIds: [], defectIds: [] };
        }
        const mh = this.props.machineHistories.find(mh => mh.id === parseInt(this.props.match.params.id!));
        if (!mh)
            return undefined;
        return {
            id: mh.id,
            machine: mh.machine,
            serviceLife: mh.serviceLife,
            date: new Date(mh.date),
            maintenanceIds: mh.machineMaintenances?.map(mm => mm.id) ?? [],
            defectIds: mh.fixedMachineDefects?.map(md => md.id) ?? [],
        };
    }

    update(changes: Partial<Draft>, clearErrors: string[] = []) {
        const errors = { ...this.state.errors };
        clearErrors.forEach(e => delete errors[e]);
        this.setState({ draft: { ...this.draft()!, ...changes }, errors });
    }

    selectedMachine(draft: Draft) {
        return this.props.machines.find(m => m.id === draft.machine.id);
    }

    selectMachine(machine: Machine) {
        const changes: Partial<Draft> = { machine, maintenanceIds: [], defectIds: [] };
        // a new entry starts from the machine's last known service life
        if (!this.isEdit())
            changes.serviceLife = machine.serviceLife;
        this.update(changes, ['machine', 'serviceLife']);
    }

    toggle(list: 'maintenanceIds' | 'defectIds', id: number, checked: boolean) {
        const ids = this.draft()![list].filter(i => i !== id);
        this.update({ [list]: checked ? [...ids, id] : ids });
    }

    maintenancesFor(machine?: Machine) {
        return machine ? this.props.machineMaintenances.filter(mm => mm.machineType.id === machine.machineType.id && !mm.archivedDate) : [];
    }

    // defects reported for the machine that no other entry has fixed yet, plus the ones this entry fixed
    openDefectsFor(machine: Machine | undefined, draft: Draft) {
        if (!machine)
            return [];
        const fixedElsewhere = new Set(this.props.machineHistories
            .filter(mh => mh.id !== draft.id)
            .flatMap(mh => mh.fixedMachineDefects?.map(md => md.id) ?? []));
        return this.props.machineHistories
            .filter(mh => mh.machine.id === machine.id)
            .flatMap(mh => mh.machineDefects ?? [])
            .filter(md => !fixedElsewhere.has(md.id) && (!md.archivedDate || draft.defectIds.includes(md.id)));
    }

    validate(draft: Draft): Errors {
        const errors: Errors = {};
        const machine = this.selectedMachine(draft);
        if (!machine) {
            errors.machine = this.t("Select a machine");
        }
        if (!draft.date) {
            errors.date = this.t("Select a date");
        }
        if (isNaN(draft.serviceLife) || draft.serviceLife < 0) {
            errors.serviceLife = this.t("Enter the service life");
        } else if (machine && !this.isEdit() && draft.serviceLife < machine.serviceLife) {
            errors.serviceLife = this.t("New service life must be greater or equal to the current one");
        }
        return errors;
    }

    validateField(fieldId: string) {
        const error = this.validate(this.draft()!)[fieldId];
        const errors = { ...this.state.errors };
        if (error)
            errors[fieldId] = error;
        else
            delete errors[fieldId];
        this.setState({ errors });
    }

    submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const draft = this.draft()!;
        const errors = this.validate(draft);
        this.setState({ errors });
        const firstError = Object.keys(errors)[0];
        if (firstError) {
            document.getElementById(firstError)?.focus();
            return;
        }
        const machine = this.selectedMachine(draft)!;
        // same shape the generic form used to post, so the backend contract stays unchanged
        const payload: WorkshopViewModel = {
            id: draft.id,
            machine,
            serviceLife: draft.serviceLife,
            date: draft.date,
            machineMaintenances: this.maintenancesFor(machine)
                .filter(mm => draft.maintenanceIds.includes(mm.id))
                .map(mm => ({ ...WorkshopMaintenance.fromMachineMaintenance(mm), checked: true })),
            fixedMachineDefects: this.openDefectsFor(machine, draft)
                .filter(md => draft.defectIds.includes(md.id))
                .map(md => ({ ...WorkshopDefect.fromMachineDefect(md, machine), checked: true })),
        };
        const save = this.isEdit() ? this.props.editMachineHistory : this.props.addMachineHistory;
        save(this.props.loginUser.token, payload as unknown as MachineHistory);
    }

    fieldError(fieldId: string) {
        const error = this.state.errors[fieldId];
        return error ? <div className="c-form__validation" id={fieldId + '-error'} role="alert">{error}</div> : null;
    }

    checkList<T extends { id: number }>(list: 'maintenanceIds' | 'defectIds', items: T[], title: (item: T) => string, description: (item: T) => string, badge?: (item: T) => React.ReactNode) {
        const selected = this.draft()![list];
        return (
            <ul className="c-form__check-list">
                {items.map(item => (
                    <li key={item.id}>
                        <label className="c-form__check">
                            <input
                                type="checkbox"
                                checked={selected.includes(item.id)}
                                onChange={e => this.toggle(list, item.id, e.currentTarget.checked)}
                            />
                            <span className="c-form__check-text">
                                <span className="c-form__check-title">{title(item)}{badge?.(item)}</span>
                                {description(item) && <span className="c-form__hint">{description(item)}</span>}
                            </span>
                        </label>
                    </li>
                ))}
            </ul>
        );
    }

    render() {
        const title = this.defectsEnabled()
            ? (this.isEdit() ? "Edit workshop entry" : "Workshop")
            : (this.isEdit() ? "Edit maintenance entry" : "Maintenance");
        if (!this.loggedIn()) {
            return <Navigate replace to="/account/login" />;
        }
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text={title} />
                    <div className="c-card" aria-busy="true"><Translator text="Loading" /></div>
                </div>
            )
        }
        const draft = this.draft();
        if (!draft) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text={title} />
                    <div className="c-card">
                        <p><Translator text="Workshop entry not found" /></p>
                        <Link className="c-button c-button--ghost" to="/Machines/histories"><Translator text="Back to history" /></Link>
                    </div>
                </div>
            )
        }

        const machine = this.selectedMachine(draft);
        const serviceLifeUnit = machine?.machineType?.serviceLifeUnit;
        const maintenances = this.maintenancesFor(machine);
        const defects = this.openDefectsFor(machine, draft);
        const machineOptions: Option<Machine>[] = this.props.machines
            .filter(m => !m.archivedDate || m.id === machine?.id)
            .map(m => ({ value: m, label: m.identification ? m.name + ' · ' + m.identification : m.name }));

        return (
            <div className="c-page__content">
                <PageContentHeader text={title} />
                <form className="c-form c-form--wide" onSubmit={(e) => this.submit(e)} noValidate>
                    <fieldset className="c-card c-form__section">
                        <legend className="c-form__section-title"><Translator text="Machine" /></legend>
                        <div className="c-form__grid">
                            <div className="c-form__field c-form__field--full">
                                <label className="c-form__label c-form__label--required" htmlFor="machine"><Translator text="Machine" /></label>
                                <Select<Option<Machine>>
                                    classNamePrefix="c-select"
                                    placeholder={this.t("Select...")}
                                    noOptionsMessage={() => this.t("No options")}
                                    inputId="machine"
                                    className={"c-form__select" + (this.state.errors.machine ? " c-form__select--invalid" : "")}
                                    options={machineOptions}
                                    value={machineOptions.find(o => o.value.id === machine?.id) ?? null}
                                    onChange={o => o && this.selectMachine(o.value)}
                                    aria-invalid={!!this.state.errors.machine}
                                    aria-errormessage="machine-error"
                                    autoFocus={!this.isEdit()}
                                />
                                {this.fieldError('machine')}
                            </div>
                            <div className="c-form__field">
                                <label className="c-form__label c-form__label--required" htmlFor="date"><Translator text="Date" /></label>
                                <ReactDatePicker
                                    id="date"
                                    className="c-form__control"
                                    selected={draft.date}
                                    onChange={(date: Date | null) => this.update({ date: date ?? undefined } as Partial<Draft>, ['date'])}
                                    dateFormat="dd.MM.yyyy HH:mm"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeCaption={this.t("Time")}
                                    maxDate={new Date()}
                                    locale="de" // todo, make this react to set language
                                    autoComplete="off"
                                    ariaInvalid={this.state.errors.date ? 'true' : 'false'}
                                    ariaDescribedBy={this.state.errors.date ? 'date-error' : undefined}
                                />
                                {this.fieldError('date')}
                            </div>
                            <div className="c-form__field">
                                <label className="c-form__label c-form__label--required" htmlFor="serviceLife"><Translator text="Service Life" /></label>
                                <div className="c-form__input-group">
                                    <input
                                        id="serviceLife"
                                        className="c-form__control"
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="any"
                                        value={isNaN(draft.serviceLife) ? '' : draft.serviceLife}
                                        onChange={e => this.update({ serviceLife: e.currentTarget.valueAsNumber }, ['serviceLife'])}
                                        onBlur={() => this.validateField('serviceLife')}
                                        aria-invalid={!!this.state.errors.serviceLife}
                                        aria-describedby={this.state.errors.serviceLife ? 'serviceLife-error' : machine ? 'serviceLife-hint' : undefined}
                                    />
                                    {serviceLifeUnit?.short && <span className="c-form__suffix" aria-hidden="true">{serviceLifeUnit.short}</span>}
                                </div>
                                {this.fieldError('serviceLife') ?? (machine &&
                                    <div className="c-form__hint" id="serviceLife-hint">
                                        {this.t("Last recorded")}: {machine.serviceLife}{serviceLifeUnit?.short ? ' ' + serviceLifeUnit.short : ''}
                                    </div>
                                )}
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className="c-card c-form__section">
                        <legend className="c-form__section-title">
                            <Translator text="Maintenance performed" /> <span className="c-form__optional"><Translator text="optional" /></span>
                        </legend>
                        {!machine
                            ? <p className="c-form__hint c-form__empty"><Translator text="Select a machine first" /></p>
                            : maintenances.length
                                ? this.checkList('maintenanceIds', maintenances, mm => mm.maintenance.name, mm => mm.maintenance.description)
                                : <p className="c-form__hint c-form__empty"><Translator text="No maintenance planned for this machine type" /></p>
                        }
                    </fieldset>

                    {this.defectsEnabled() && <fieldset className="c-card c-form__section">
                        <legend className="c-form__section-title">
                            <Translator text="Defects fixed" /> <span className="c-form__optional"><Translator text="optional" /></span>
                        </legend>
                        {!machine
                            ? <p className="c-form__hint c-form__empty"><Translator text="Select a machine first" /></p>
                            : defects.length
                                ? this.checkList('defectIds', defects, (md: MachineDefect) => md.defect, md => md.description, md => md.outOfOrderFlag &&
                                    <span className="c-form__badge"><FontAwesomeIcon icon={faTriangleExclamation} /> <Translator text="Out of order" /></span>)
                                : <p className="c-form__hint c-form__empty"><Translator text="No open defects for this machine" /></p>
                        }
                    </fieldset>}

                    <div className="c-form__actions">
                        <Link className="c-button c-button--ghost" to="/Machines/histories"><Translator text="Cancel" /></Link>
                        <button className="c-button c-button--primary" type="submit" disabled={this.props.processing} aria-busy={this.props.processing}>
                            {this.props.processing ? <div className="c-button__loader" /> : <Translator text="Save" />}
                        </button>
                    </div>
                </form>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineHistories: state.machineHistories,
        machineMaintenances: state.machineMaintenances,
        processing: state.processingRequest,
        currentSubscription: state.currentSubscription,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        editMachineHistory: bindActionCreators(MachineHistoryActions.editMachineHistory, dispatch),
        addMachineHistory: bindActionCreators(MachineHistoryActions.addMachineHistory, dispatch),
    };
}
export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(WorkshopComp));
